import { useState, useEffect, MouseEvent } from 'react';
import { Preferences } from '@capacitor/preferences';

import { MILLISECONDS_IN_A_DAY } from '../utils/group-expirations-by-date';

const POINT_EXPIRATION_DISPLAY_DISMISSED_AT = 'POINT_EXPIRATION_DISPLAY_DISMISSED_AT';

const DISMISSAL_STATES = ['NOT_DISMISSED', 'LOADING_PREFERENCES', 'RECENT', 'MANUAL'] as const;

export const useDismissal = (notificationDays: number) => {
  const [dismissed, setDismissed] = useState<(typeof DISMISSAL_STATES)[number]>('LOADING_PREFERENCES');

  const handleManualDismissal = async (event: MouseEvent) => {
    event.stopPropagation();
    await Preferences.set({ key: POINT_EXPIRATION_DISPLAY_DISMISSED_AT, value: new Date().toISOString() });
    setDismissed('MANUAL');
  };

  useEffect(() => {
    let cancelled = false;

    const checkStatus = async () => {
      try {
        setDismissed('LOADING_PREFERENCES');
        const { value } = await Preferences.get({ key: POINT_EXPIRATION_DISPLAY_DISMISSED_AT });

        if (cancelled) return;

        if (value) {
          const daysSinceDismissed = Math.floor((Date.now() - new Date(value).getTime()) / MILLISECONDS_IN_A_DAY);
          setDismissed((dismissedState) => {
            if (dismissedState === 'MANUAL') return 'MANUAL';
            return daysSinceDismissed < notificationDays ? 'RECENT' : 'NOT_DISMISSED';
          });
        } else {
          setDismissed((dismissedState) => {
            if (dismissedState === 'MANUAL') return 'MANUAL';
            return 'NOT_DISMISSED';
          });
        }
      } catch (error) {
        if (!cancelled) {
          setDismissed((dismissedState) => {
            if (dismissedState === 'MANUAL') return 'MANUAL';
            return 'NOT_DISMISSED';
          });
        }
      }
    };

    checkStatus();

    return () => {
      cancelled = true;
    };
  }, [notificationDays]);

  return {
    dismissed,
    handleManualDismissal,
  };
};
